import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Screen from '../../components/common/Screen';
import Card from '../../components/elements/Card';
import Button from '../../components/elements/Button';

const BRAND = '#2596be';
const BRAND_DARK = '#1b7898';
const BRAND_SOFT = '#e7f6fb';
const LOGO = require('./../../assets/images/e7group.png');

const previewRoutes = [
  { to: '/e7-preview/login', label: 'Welcome / Login' },
  { to: '/e7-preview/start', label: 'Survey Start' },
  { to: '/e7-preview/scale-10', label: 'Big Survey / Dots to 10' },
  { to: '/e7-preview/sliders', label: 'Big Survey / Sliders to 5' },
  { to: '/e7-preview/pulse-hi', label: 'Pulse Survey / Hindi' },
  { to: '/e7-preview/qr', label: 'QR / No Email' },
  { to: '/e7-preview/dashboard-ar', label: 'Desktop Dashboard / Arabic' },
];

const pageShellStyle = {
  width: '100%',
  padding: '1.25rem 0.85rem 2rem',
  display: 'flex',
  justifyContent: 'center',
};

const phoneStyle = {
  width: 'min(100%, 27rem)',
  minHeight: '42rem',
  borderRadius: '28px',
  background: 'linear-gradient(180deg, #ffffff 0%, #f4fbfe 100%)',
  boxShadow: '0 26px 70px rgba(37, 150, 190, 0.2)',
  border: '1px solid rgba(37, 150, 190, 0.18)',
  overflow: 'hidden',
};

const desktopShellStyle = {
  width: 'min(74rem, calc(100vw - 2rem))',
  minWidth: '68rem',
  minHeight: '44rem',
  borderRadius: '30px',
  background: 'linear-gradient(180deg, #ffffff 0%, #f4fbfe 100%)',
  boxShadow: '0 28px 80px rgba(37, 150, 190, 0.18)',
  border: '1px solid rgba(37, 150, 190, 0.18)',
  overflow: 'hidden',
};

const contentStyle = {
  padding: '1.35rem',
  textAlign: 'left',
};

const primaryButtonStyle = {
  width: '100%',
  backgroundColor: BRAND,
  color: '#fff',
  borderRadius: '14px',
  fontWeight: 600,
};

const secondaryButtonStyle = {
  width: '100%',
  backgroundColor: '#fff',
  color: BRAND,
  border: `1px solid ${BRAND}`,
  borderRadius: '14px',
  fontWeight: 600,
};

const inputStyle = {
  width: '100%',
  border: '1px solid #c7dfe8',
  borderRadius: '14px',
  padding: '0.9rem 1rem',
  outline: 'none',
  fontSize: '0.95rem',
  backgroundColor: '#fff',
};

const cardTitleStyle = {
  fontSize: '1.65rem',
  fontWeight: 700,
  color: '#11313f',
  lineHeight: 1.2,
  marginBottom: '0.6rem',
};

const smallMutedStyle = {
  color: '#5f7782',
  fontSize: '0.95rem',
  lineHeight: 1.55,
};

const scoreLabels = ['Strongly disagree', 'Disagree', 'Slightly disagree', 'Neutral', 'Slightly agree', 'Agree', 'Strongly agree'];

const qrPattern = [
  '11100101101',
  '10010111001',
  '10111000101',
  '00101110110',
  '11100010101',
  '01011101010',
  '10100111100',
  '01110001011',
  '11010100101',
  '10001111010',
  '10110100111',
];

const Header = ({ title, subtitle }) => (
  <div style={{ padding: '1rem 1.35rem 0.5rem', textAlign: 'left' }}>
    <div
      style={{
        background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_DARK} 100%)`,
        borderRadius: '24px',
        padding: '1.1rem 1.2rem',
        color: '#fff',
      }}
    >
      <img src={LOGO} alt="E7 Group" style={{ width: '7rem', height: 'auto', marginBottom: '1rem' }} />
      <div style={{ fontSize: '1.45rem', fontWeight: 700, lineHeight: 1.2 }}>{title}</div>
      <div style={{ opacity: 0.9, marginTop: '0.45rem', fontSize: '0.95rem', lineHeight: 1.5 }}>{subtitle}</div>
    </div>
  </div>
);

const ProgressDots = ({ active }) => (
  <div style={{ display: 'flex', gap: '0.5rem', justifyContent: 'center', marginBottom: '1rem' }}>
    {[1, 2, 3, 4, 5].map((dot) => (
      <span
        key={dot}
        style={{
          width: dot === active ? '1.7rem' : '0.65rem',
          height: '0.65rem',
          borderRadius: '999px',
          backgroundColor: dot <= active ? BRAND : '#cfe8f0',
          display: 'inline-block',
        }}
      />
    ))}
  </div>
);

const ScoreDots = () => (
  <div style={{ marginTop: '1.2rem' }}>
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(10, minmax(0, 1fr))', gap: '0.45rem' }}>
      {Array.from({ length: 10 }, (_, index) => {
        const value = index + 1;
        const isSelected = value === 8;

        return (
          <div key={value} style={{ textAlign: 'center' }}>
            <div
              style={{
                width: '100%',
                aspectRatio: '1 / 1',
                borderRadius: '999px',
                border: `2px solid ${isSelected ? BRAND : '#9dc9d8'}`,
                backgroundColor: isSelected ? BRAND : '#fff',
                color: isSelected ? '#fff' : '#2b5d6f',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: 700,
              }}
            >
              {value}
            </div>
          </div>
        );
      })}
    </div>
    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '0.65rem', color: '#5f7782', fontSize: '0.8rem' }}>
      <span>Not likely</span>
      <span>Very likely</span>
    </div>
  </div>
);

const SliderQuestion = ({ question, value }) => (
  <div style={{ marginBottom: '1.25rem' }}>
    <div style={{ color: '#173846', fontWeight: 600, marginBottom: '0.55rem', lineHeight: 1.45 }}>{question}</div>
    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
      <span style={{ color: '#6b848e', fontSize: '0.8rem' }}>1</span>
      <div style={{ position: 'relative', flex: 1, height: '0.55rem', borderRadius: '999px', backgroundColor: '#d9edf4' }}>
        <div
          style={{
            position: 'absolute',
            inset: 0,
            width: `${((value - 1) / 4) * 100}%`,
            background: `linear-gradient(90deg, ${BRAND} 0%, ${BRAND_DARK} 100%)`,
            borderRadius: '999px',
          }}
        />
        <div
          style={{
            position: 'absolute',
            top: '50%',
            left: `calc(${((value - 1) / 4) * 100}% - 0.6rem)`,
            transform: 'translateY(-50%)',
            width: '1.2rem',
            height: '1.2rem',
            borderRadius: '999px',
            backgroundColor: '#fff',
            border: `3px solid ${BRAND}`,
            boxShadow: '0 4px 10px rgba(37, 150, 190, 0.18)',
          }}
        />
      </div>
      <span style={{ color: BRAND_DARK, fontWeight: 700 }}>{value}</span>
    </div>
  </div>
);

const PulseOption = ({ emoji, text, active }) => (
  <div
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '0.9rem',
      padding: '0.95rem 1rem',
      borderRadius: '16px',
      border: `1px solid ${active ? BRAND : '#d5e8ef'}`,
      backgroundColor: active ? BRAND_SOFT : '#fff',
      marginBottom: '0.8rem',
    }}
  >
    <div style={{ fontSize: '1.4rem' }}>{emoji}</div>
    <div style={{ flex: 1, color: '#143643', fontWeight: 600 }}>{text}</div>
    <div
      style={{
        width: '1.1rem',
        height: '1.1rem',
        borderRadius: '999px',
        backgroundColor: active ? BRAND : '#fff',
        border: `2px solid ${active ? BRAND : '#9dc9d8'}`,
      }}
    />
  </div>
);

const QrGrid = () => (
  <div
    style={{
      width: '12rem',
      height: '12rem',
      margin: '0 auto',
      display: 'grid',
      gridTemplateColumns: 'repeat(11, 1fr)',
      gap: '0.18rem',
      backgroundColor: '#fff',
      padding: '0.7rem',
      borderRadius: '18px',
      boxShadow: 'inset 0 0 0 1px #d5e8ef',
    }}
  >
    {qrPattern.flatMap((row, rowIndex) =>
      row.split('').map((cell, cellIndex) => (
        <span
          key={`${rowIndex}-${cellIndex}`}
          style={{
            backgroundColor: cell === '1' ? '#143643' : 'transparent',
            borderRadius: '0.12rem',
          }}
        />
      ))
    )}
  </div>
);

const PreviewFrame = ({ children }) => (
  <Screen color="#eff8fb">
    <div className="center" style={pageShellStyle}>
      <div style={phoneStyle}>{children}</div>
    </div>
  </Screen>
);

const DesktopPreviewFrame = ({ children }) => (
  <div
    style={{
      minHeight: '100vh',
      width: '100%',
      background: '#eff8fb',
      padding: '1.25rem 0.85rem 2rem',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      overflowX: 'auto',
      boxSizing: 'border-box',
    }}
  >
    <div style={desktopShellStyle}>{children}</div>
  </div>
);

const PreviewHub = () => (
  <PreviewFrame>
    <Header title="E7 Survey Preview" subtitle="Hardcoded review screens for screenshot selection." />
    <div style={contentStyle}>
      <Card style={{ backgroundColor: BRAND_SOFT, border: '1px solid #c9e8f2', boxShadow: 'none' }} mx="0" mb="3">
        <div style={{ ...cardTitleStyle, fontSize: '1.2rem', marginBottom: '0.35rem' }}>Available previews</div>
        <div style={smallMutedStyle}>Open any screen below to capture the exact state your boss asked for.</div>
      </Card>

      {previewRoutes.map((route) => (
        <Link
          key={route.to}
          to={route.to}
          className="link"
          style={{
            display: 'block',
            padding: '0.95rem 1rem',
            backgroundColor: '#fff',
            borderRadius: '16px',
            border: '1px solid #d5e8ef',
            marginBottom: '0.8rem',
            color: '#173846',
            fontWeight: 600,
          }}
        >
          {route.label}
        </Link>
      ))}
    </div>
  </PreviewFrame>
);

const WelcomeLogin = () => (
  <PreviewFrame>
    <Header title="Welcome to the E7 employee survey" subtitle="Sign in to access your assigned survey securely." />
    <div style={contentStyle}>
      <ProgressDots active={1} />
      <div style={cardTitleStyle}>Let&apos;s get you started</div>
      <div style={{ ...smallMutedStyle, marginBottom: '1.1rem' }}>
        Use your work email and access code to open the survey invitation.
      </div>

      <div style={{ marginBottom: '0.45rem', fontWeight: 600, color: '#173846' }}>Work email</div>
      <input readOnly value="employee@e7group.com" style={{ ...inputStyle, marginBottom: '1rem' }} />

      <div style={{ marginBottom: '0.45rem', fontWeight: 600, color: '#173846' }}>Access code</div>
      <input readOnly value="E7-2026-0148" style={{ ...inputStyle, marginBottom: '1.2rem' }} />

      <Button theme="light" styles={primaryButtonStyle}>Continue</Button>
      <Button theme="light" styles={secondaryButtonStyle} mb="0">Need help?</Button>
    </div>
  </PreviewFrame>
);

const SurveyStart = () => (
  <PreviewFrame>
    <Header title="Safety & engagement survey" subtitle="Welcome back, Priya. Your feedback helps E7 improve every location." />
    <div style={contentStyle}>
      <Card style={{ backgroundColor: '#fff', border: '1px solid #d5e8ef', boxShadow: 'none' }} mx="0" mb="3">
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.65rem', color: BRAND_DARK, fontWeight: 700 }}>
          <span>Survey details</span>
          <span>6 mins</span>
        </div>
        <div style={smallMutedStyle}>
          Anonymous responses, mobile friendly, and available in multiple languages.
        </div>
      </Card>

      <div style={{ ...cardTitleStyle, marginBottom: '0.45rem' }}>Before you begin</div>
      <div style={{ ...smallMutedStyle, marginBottom: '1rem' }}>
        Answer honestly. Results are reviewed in aggregate to identify trends, strengths, and areas that need support.
      </div>

      <div style={{ display: 'grid', gap: '0.8rem', marginBottom: '1.25rem' }}>
        {[
          'Your answers remain confidential.',
          'You can complete the survey in one sitting.',
          'Questions focus on team experience and operations.',
        ].map((item) => (
          <div key={item} style={{ display: 'flex', gap: '0.7rem', alignItems: 'flex-start' }}>
            <span style={{ width: '1.35rem', height: '1.35rem', borderRadius: '999px', backgroundColor: BRAND_SOFT, color: BRAND, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700 }}>✓</span>
            <span style={{ color: '#173846', lineHeight: 1.45 }}>{item}</span>
          </div>
        ))}
      </div>

      <Button theme="light" styles={primaryButtonStyle}>Start survey</Button>
    </div>
  </PreviewFrame>
);

const BigSurveyDots = () => (
  <PreviewFrame>
    <Header title="Big survey" subtitle="How likely are you to recommend E7 Group as a good place to work?" />
    <div style={contentStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#6b848e', fontSize: '0.85rem', marginBottom: '0.7rem' }}>
        <span>Question 8 of 18</span>
        <span>Scale 1-10</span>
      </div>
      <div style={{ height: '0.55rem', borderRadius: '999px', backgroundColor: '#d9edf4', overflow: 'hidden', marginBottom: '1.2rem' }}>
        <div style={{ width: '44%', height: '100%', background: `linear-gradient(90deg, ${BRAND} 0%, ${BRAND_DARK} 100%)` }} />
      </div>

      <div style={{ ...cardTitleStyle, fontSize: '1.35rem' }}>
        I would recommend E7 Group to friends or family looking for work.
      </div>
      <div style={{ ...smallMutedStyle, marginBottom: '0.85rem' }}>
        Choose a number from 1 to 10 that best reflects your opinion.
      </div>

      <ScoreDots />

      <div style={{ marginTop: '1.3rem', padding: '1rem', backgroundColor: BRAND_SOFT, borderRadius: '16px', color: '#173846', fontSize: '0.9rem' }}>
        Selected answer: <strong>8</strong> ({scoreLabels[5]})
      </div>

      <div style={{ display: 'flex', gap: '0.8rem', marginTop: '1.2rem' }}>
        <Button theme="light" styles={{ ...secondaryButtonStyle, marginBottom: 0 }}>Previous</Button>
        <Button theme="light" styles={{ ...primaryButtonStyle, marginBottom: 0 }}>Next</Button>
      </div>
    </div>
  </PreviewFrame>
);

const BigSurveySliders = () => (
  <PreviewFrame>
    <Header title="Big survey" subtitle="Rate the following statements on a scale from 1 to 5." />
    <div style={contentStyle}>
      <div style={{ display: 'flex', justifyContent: 'space-between', color: '#6b848e', fontSize: '0.85rem', marginBottom: '0.7rem' }}>
        <span>Question 11 of 18</span>
        <span>Sliders 1-5</span>
      </div>
      <div style={{ height: '0.55rem', borderRadius: '999px', backgroundColor: '#d9edf4', overflow: 'hidden', marginBottom: '1.15rem' }}>
        <div style={{ width: '61%', height: '100%', background: `linear-gradient(90deg, ${BRAND} 0%, ${BRAND_DARK} 100%)` }} />
      </div>

      <div style={{ ...smallMutedStyle, marginBottom: '1rem' }}>
        1 = strongly disagree, 5 = strongly agree
      </div>

      <SliderQuestion question="Communication from management is clear and timely." value={4} />
      <SliderQuestion question="I have the tools I need to do my job well." value={5} />
      <SliderQuestion question="My workload feels manageable most of the time." value={3} />

      <Button theme="light" styles={primaryButtonStyle}>Continue</Button>
    </div>
  </PreviewFrame>
);

const PulseSurveyHindi = () => (
  <PreviewFrame>
    <Header title="Pulse survey" subtitle="Short check-in for this week. Hindi preview version." />
    <div style={contentStyle}>
      <div style={{ ...cardTitleStyle, fontSize: '1.35rem' }}>आज आपका काम का अनुभव कैसा रहा?</div>
      <div style={{ ...smallMutedStyle, marginBottom: '1rem' }}>
        एक उत्तर चुनें जो आपके आज के अनुभव के सबसे करीब हो।
      </div>

      <PulseOption emoji="😀" text="बहुत अच्छा" active={false} />
      <PulseOption emoji="🙂" text="अच्छा" active={true} />
      <PulseOption emoji="😐" text="ठीक-ठाक" active={false} />
      <PulseOption emoji="🙁" text="चुनौतीपूर्ण" active={false} />

      <div style={{ marginTop: '1rem', marginBottom: '1rem' }}>
        <div style={{ marginBottom: '0.45rem', fontWeight: 600, color: '#173846' }}>कोई टिप्पणी?</div>
        <div style={{ ...inputStyle, minHeight: '6.5rem', color: '#6b848e' }}>
          आज टीम का सहयोग अच्छा था और शिफ्ट की जानकारी समय पर मिली।
        </div>
      </div>

      <Button theme="light" styles={primaryButtonStyle}>जमा करें</Button>
    </div>
  </PreviewFrame>
);

const QrPage = () => (
  <PreviewFrame>
    <Header title="No email address?" subtitle="Employees without email can still join the survey using a shared QR code." />
    <div style={contentStyle}>
      <div style={{ ...cardTitleStyle, fontSize: '1.3rem', textAlign: 'center' }}>Scan to open the survey</div>
      <div style={{ ...smallMutedStyle, textAlign: 'center', marginBottom: '1.2rem' }}>
        Supervisors can display this code in the break room or on a team noticeboard.
      </div>

      <div style={{ backgroundColor: BRAND_SOFT, borderRadius: '24px', padding: '1.2rem', marginBottom: '1rem' }}>
        <QrGrid />
      </div>

      <Card style={{ backgroundColor: '#fff', border: '1px solid #d5e8ef', boxShadow: 'none' }} mx="0" mb="3">
        <div style={{ color: '#173846', fontWeight: 700, marginBottom: '0.4rem' }}>Alternative text for posters</div>
        <div style={smallMutedStyle}>
          Don&apos;t have a company email? Scan this QR code and enter your location code to start the survey.
        </div>
      </Card>

      <Button theme="light" styles={primaryButtonStyle}>Open survey link</Button>
    </div>
  </PreviewFrame>
);

function DesktopDashboardArabic() {
  useEffect(() => {
    document.body.classList.add('desktop-preview-route');
    const root = document.getElementById('root');
    root?.classList.add('desktop-preview-root');

    return () => {
      document.body.classList.remove('desktop-preview-route');
      root?.classList.remove('desktop-preview-root');
    };
  }, []);

  return (
    <DesktopPreviewFrame>
      <div dir="rtl" style={{ minHeight: '100%', display: 'grid', gridTemplateColumns: '18rem minmax(0, 1fr)' }}>
        <div
          style={{
            background: 'linear-gradient(180deg, #0f4d63 0%, #143643 100%)',
            color: '#fff',
            padding: '1.5rem 1.25rem',
            textAlign: 'right',
          }}
        >
          <img src={LOGO} alt="E7 Group" style={{ width: '7.2rem', height: 'auto', marginBottom: '1.5rem' }} />
          <div style={{ fontSize: '0.8rem', opacity: 0.75, marginBottom: '0.35rem' }}>لوحة الإدارة</div>
          <div style={{ fontSize: '1.35rem', fontWeight: 700, lineHeight: 1.35, marginBottom: '1.5rem' }}>
            متابعة حالة الاستبيانات عبر المواقع
          </div>

          {['نظرة عامة', 'المشاركات', 'النتائج', 'اللغات', 'رمز QR'].map((item, index) => (
            <div
              key={item}
              style={{
                padding: '0.85rem 1rem',
                borderRadius: '16px',
                marginBottom: '0.55rem',
                backgroundColor: index === 0 ? 'rgba(255,255,255,0.14)' : 'transparent',
                border: index === 0 ? '1px solid rgba(255,255,255,0.16)' : '1px solid transparent',
                fontWeight: 600,
              }}
            >
              {item}
            </div>
          ))}

          <div
            style={{
              marginTop: '1.5rem',
              padding: '1rem',
              borderRadius: '20px',
              backgroundColor: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.12)',
            }}
          >
            <div style={{ fontSize: '0.8rem', opacity: 0.8, marginBottom: '0.25rem' }}>آخر تحديث</div>
            <div style={{ fontWeight: 700 }}>05 أبريل 2026</div>
          </div>
        </div>

        <div style={{ padding: '1.5rem', textAlign: 'right' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '1rem',
              marginBottom: '1.35rem',
              flexDirection: 'row-reverse',
            }}
          >
            <div>
              <div style={{ color: '#5f7782', fontSize: '0.9rem', marginBottom: '0.3rem' }}>ملخص اليوم</div>
              <div style={{ fontSize: '1.9rem', fontWeight: 700, color: '#11313f' }}>لوحة متابعة استبيان E7</div>
            </div>
            <div
              style={{
                minWidth: '13rem',
                padding: '0.95rem 1rem',
                borderRadius: '18px',
                backgroundColor: BRAND_SOFT,
                color: BRAND_DARK,
                fontWeight: 700,
                textAlign: 'center',
              }}
            >
              82% معدل الاستجابة
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: '1rem', marginBottom: '1.2rem' }}>
            {[
              ['1,248', 'إجمالي الدعوات'],
              ['1,022', 'الردود المكتملة'],
              ['14', 'المواقع النشطة'],
              ['3', 'اللغات المتاحة'],
            ].map(([value, label]) => (
              <Card key={label} style={{ boxShadow: 'none', border: '1px solid #d5e8ef' }} mx="0" mb="0">
                <div style={{ fontSize: '1.7rem', fontWeight: 700, color: BRAND_DARK, marginBottom: '0.35rem' }}>{value}</div>
                <div style={{ color: '#5f7782' }}>{label}</div>
              </Card>
            ))}
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1.25fr 0.95fr', gap: '1rem' }}>
            <Card style={{ boxShadow: 'none', border: '1px solid #d5e8ef' }} mx="0" mb="0">
              <div style={{ fontSize: '1.1rem', fontWeight: 700, color: '#11313f', marginBottom: '1rem' }}>أداء المواقع</div>
              {[
                ['أبوظبي', 91],
                ['دبي', 84],
                ['الرياض', 76],
                ['الدمام', 68],
              ].map(([site, value]) => (
                <div key={site} style={{ marginBottom: '0.95rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', flexDirection: 'row-reverse', marginBottom: '0.35rem', color: '#173846', fontWeight: 600 }}>
                    <span>{site}</span>
                    <span>{value}%</span>
                  </div>
                  <div style={{ height: '0.65rem', borderRadius: '999px', backgroundColor: '#d9edf4', overflow: 'hidden' }}>
                    <div style={{ width: `${value}%`, height: '100%', background: `linear-gradient(90deg, ${BRAND} 0%, ${BRAND_DARK} 100%)` }} />
                  </div>
                </div>
              ))}
            </Card>

            <div style={{ display: 'grid', gap: '1rem' }}>
              <Card style={{ boxShadow: 'none', border: '1px solid #d5e8ef' }} mx="0" mb="0">
                <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#11313f', marginBottom: '0.7rem' }}>أهم النتائج</div>
                <div style={{ color: '#173846', lineHeight: 1.8 }}>
                  <div>78% يشعرون أن التواصل واضح.</div>
                  <div>83% راضون عن بيئة العمل.</div>
                  <div>69% يريدون تدريبًا إضافيًا.</div>
                </div>
              </Card>

              <Card style={{ boxShadow: 'none', border: '1px solid #d5e8ef', backgroundColor: BRAND_SOFT }} mx="0" mb="0">
                <div style={{ fontSize: '1.05rem', fontWeight: 700, color: '#11313f', marginBottom: '0.55rem' }}>إجراء مقترح</div>
                <div style={{ color: '#173846', lineHeight: 1.7, marginBottom: '0.9rem' }}>
                  مشاركة رمز QR في المواقع ذات الاستجابة المنخفضة لرفع نسبة المشاركة بسرعة.
                </div>
                <Button theme="light" styles={{ ...primaryButtonStyle, marginBottom: 0 }}>تصدير التقرير</Button>
              </Card>
            </div>
          </div>
        </div>
      </div>
    </DesktopPreviewFrame>
  );
}

const E7SurveyPreview = ({ variant = 'hub' }) => {
  const variants = {
    hub: <PreviewHub />,
    login: <WelcomeLogin />,
    start: <SurveyStart />,
    'scale-10': <BigSurveyDots />,
    sliders: <BigSurveySliders />,
    'pulse-hi': <PulseSurveyHindi />,
    qr: <QrPage />,
    'dashboard-ar': <DesktopDashboardArabic />,
  };

  return variants[variant] || <PreviewHub />;
};

export default E7SurveyPreview;
